import { KeycloakService } from "@/services/keycloakService";
import { AuthContextType, AuthState } from "@/types/auth";
import React, { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    accessToken: null,
    error: null,
  });

  // Verificar se há um token válido ao carregar a aplicação
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = KeycloakService.getAccessToken();
        const hasValidToken = KeycloakService.hasValidToken();

        let userInfo = null;
        if (hasValidToken) {
          // Tentar recuperar informações do usuário do storage primeiro
          userInfo = KeycloakService.getUserInfoFromStorage();

          // Se não estiver no storage, buscar do Keycloak
          if (!userInfo) {
            try {
              userInfo = await KeycloakService.getUserInfo();
              KeycloakService.storeUserInfo(userInfo);
            } catch (userError) {
              console.warn(
                "[KEYCLOAK-AUTH] Erro ao obter informações do usuário:",
                userError
              );
              // Continua sem informações do usuário, mas mantém autenticação
            }
          }
        }

        setAuthState({
          isAuthenticated: hasValidToken,
          isLoading: false,
          user: userInfo,
          accessToken: token,
          error: null,
        });
      } catch (error) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          accessToken: null,
          error: error instanceof Error ? error.message : "Erro desconhecido",
        });
      }
    };

    checkAuth();
  }, []);

  // Validação periódica do token (a cada 30 segundos)
  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const interval = setInterval(async () => {
      const isValid = await KeycloakService.validateToken();

      if (!isValid) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          accessToken: null,
          error: "Sessão expirada",
        });
      }
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [authState.isAuthenticated]);

  // Validação do token quando a página ganha foco
  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const handleFocus = async () => {
      const isValid = await KeycloakService.validateToken();

      if (!isValid) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          accessToken: null,
          error: "Sessão expirada",
        });
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [authState.isAuthenticated]);

  const login = () => {
    KeycloakService.redirectToLogin();
  };

  const logout = () => {
    KeycloakService.logout();
  };

  const checkAuth = async (): Promise<boolean> => {
    // Primeiro verifica se há token localmente
    const hasToken = KeycloakService.hasValidToken();
    if (!hasToken) {
      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: false,
        accessToken: null,
        user: null,
      }));
      return false;
    }

    // Se há token, valida com o Keycloak
    const isValidToken = await KeycloakService.validateToken();

    setAuthState((prev) => ({
      ...prev,
      isAuthenticated: isValidToken,
      accessToken: isValidToken ? KeycloakService.getAccessToken() : null,
      user: isValidToken ? prev.user : null,
    }));

    return isValidToken;
  };

  const getAccessToken = (): string | null => {
    return KeycloakService.getAccessToken();
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    checkAuth,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
